import {
  Container,
  FillGradient,
  Filter,
  GlProgram,
  Graphics,
  Rectangle,
  RenderTexture,
  Sprite,
  type Application,
} from 'pixi.js';
import { TILE_HALF } from '../sim/projection';
import { resolveLightTexture, type LightTextures } from './lightArt';

/** Exponential fade rate for dungeon lighting, per millisecond. */
export const DUNGEON_LIGHT_FADE_PER_MS = 0.006;

/** Readable remembered darkness outside the live player light. */
export const DUNGEON_REMEMBERED_LEVEL = 0.32;

/** Backward-compatible name for the lightmap's ambient floor. */
export const DUNGEON_AMBIENT_LEVEL = DUNGEON_REMEMBERED_LEVEL;

/** Player light radius in authored screen pixels at zoom 1. */
export const DUNGEON_PLAYER_LIGHT_RADIUS = 320;

/** Plain radial reveal in dungeon tile space, independently tunable from live light. */
export const DUNGEON_SIGHT_RADIUS_TILES = 10.5;

/** GPU blend operation that makes every sight texel monotonic within a run. */
export const DUNGEON_SIGHT_BLEND_MODE = 'max';

/** Raise the light from the player's feet toward the body. */
export const DUNGEON_PLAYER_LIGHT_Y_OFFSET = -38;

const DUNGEON_LIGHT_SNAP_EPSILON = 0.001;

export interface DungeonStaticLight {
  x: number;
  y: number;
  profile: number;
  color: number;
  shader?: number;
}

export interface DungeonSightRun {
  runKey: string;
  mapWidth: number;
  mapHeight: number;
}

export interface DungeonSightTarget extends DungeonSightRun {
  playerTile: { x: number; y: number };
}

export interface DungeonSightStampPlan {
  state: DungeonSightRun;
  clear: boolean;
}

/** CPU reference for the sight texture's GPU max blend, used to pin its invariant. */
export function accumulateDungeonSightSample(existing: number, incoming: number): number {
  return Math.max(existing, incoming);
}

/**
 * Decide whether the next sight stamp starts a fresh mask.
 *
 * Same-run movement never clears, so max-blended stamps accumulate monotonically.
 */
export function planDungeonSightStamp(
  current: DungeonSightRun | undefined,
  next: DungeonSightRun,
): DungeonSightStampPlan {
  return {
    state: next,
    clear:
      current === undefined ||
      current.runKey !== next.runKey ||
      current.mapWidth !== next.mapWidth ||
      current.mapHeight !== next.mapHeight,
  };
}

/**
 * Ease the live dungeon-light amount toward the requested state.
 *
 * Exponential smoothing makes the transition independent of frame rate.
 * A zero-duration update deliberately holds the current value.
 */
export function advanceDungeonLight(current: number, enabled: boolean, dtMs: number): number {
  const target = enabled ? 1 : 0;
  if (dtMs <= 0 || current === target) return current;
  const k = 1 - Math.exp(-DUNGEON_LIGHT_FADE_PER_MS * dtMs);
  const next = current + (target - current) * k;
  return Math.abs(target - next) < DUNGEON_LIGHT_SNAP_EPSILON ? target : next;
}

/** Convert the screen viewport into a translated and scaled world's local space. */
export function dungeonFilterArea(
  worldPosition: { x: number; y: number },
  worldScale: number,
  screen: { width: number; height: number },
): { x: number; y: number; width: number; height: number } {
  const scale = Math.abs(worldScale) || 1;
  return {
    x: -worldPosition.x / scale,
    y: -worldPosition.y / scale,
    width: screen.width / scale,
    height: screen.height / scale,
  };
}

const vertex = /* glsl */ `
  in vec2 aPosition;
  out vec2 vTextureCoord;
  out vec2 vLightCoord;
  out vec2 vWorldPosition;
  uniform vec4 uInputSize;
  uniform vec4 uOutputFrame;
  uniform vec4 uOutputTexture;
  uniform vec4 uSightFrame;
  vec4 filterVertexPosition() {
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
  }
  vec2 filterTextureCoord() {
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
  }
  void main(void) {
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
    vLightCoord = aPosition;
    vWorldPosition = aPosition * uSightFrame.zw + uSightFrame.xy;
  }
`;

// Pixi rewrites this exact output declaration for its WebGL1 path.
const fragment = /* glsl */ `
  in vec2 vTextureCoord;
  in vec2 vLightCoord;
  in vec2 vWorldPosition;
  out vec4 finalColor;
  uniform sampler2D uTexture;
  uniform sampler2D uLightmap;
  uniform sampler2D uSightMask;
  uniform float uStrength;
  uniform float uSightEnabled;
  uniform vec2 uSightSize;

  void main(void) {
    vec4 src = texture(uTexture, vTextureCoord);
    vec3 light = clamp(texture(uLightmap, vLightCoord).rgb, 0.0, 1.0);
    float tileX = (vWorldPosition.x + vWorldPosition.y) / ${TILE_HALF * 2}.0;
    float tileY = (vWorldPosition.y - vWorldPosition.x) / ${TILE_HALF * 2}.0;
    vec2 sightCoord = (vec2(tileX, tileY) + vec2(0.5)) / uSightSize;
    float inBounds =
      step(0.0, sightCoord.x) * step(sightCoord.x, 1.0) *
      step(0.0, sightCoord.y) * step(sightCoord.y, 1.0);
    float seen = texture(uSightMask, sightCoord).r * inBounds;
    float sight = mix(1.0, seen, uSightEnabled);
    vec3 multiplier = mix(vec3(1.0), light * sight, uStrength);
    finalColor = vec4(src.rgb * multiplier, src.a);
  }
`;

function gray(value: number): number {
  const channel = Math.round(Math.min(1, Math.max(0, value)) * 255);
  return (channel << 16) | (channel << 8) | channel;
}

function createDungeonLightFilter(
  lightmap: RenderTexture,
  sightMask: RenderTexture,
  sightFrame: Float32Array,
  sightSize: Float32Array,
): Filter {
  return new Filter({
    glProgram: GlProgram.from({ vertex, fragment }),
    resources: {
      uLightmap: lightmap.source,
      uSightMask: sightMask.source,
      dungeonLightUniforms: {
        uStrength: { value: 0, type: 'f32' },
        uSightEnabled: { value: 0, type: 'f32' },
        uSightFrame: { value: sightFrame, type: 'vec4<f32>' },
        uSightSize: { value: sightSize, type: 'vec2<f32>' },
      },
    },
  });
}

/**
 * The render-only dungeon lightmap.
 *
 * The interface stays at one target-driven update. Internally it owns the
 * screen-sized render texture, additive light geometry, multiply filter,
 * viewport resize, filter ordering, and teardown.
 */
export class DungeonLighting {
  private readonly lightmap = RenderTexture.create({
    width: 1,
    height: 1,
    resolution: 1,
    dynamic: true,
  });
  private readonly sightMask = RenderTexture.create({
    width: 1,
    height: 1,
    resolution: 1,
    dynamic: true,
    scaleMode: 'linear',
  });
  private readonly sightFrame = new Float32Array([0, 0, 1, 1]);
  private readonly sightSize = new Float32Array([1, 1]);
  private readonly filter = createDungeonLightFilter(
    this.lightmap,
    this.sightMask,
    this.sightFrame,
    this.sightSize,
  );
  private readonly lightLayer = new Container();
  private readonly sightLayer = new Container();
  private readonly ambient = new Graphics();
  private readonly playerLight = new Graphics();
  private readonly staticLights = new Container();
  private readonly staticLightSprites: Sprite[] = [];
  private readonly sightStamp = new Graphics();
  private readonly playerGradient = new FillGradient({
    type: 'radial',
    center: { x: 0.5, y: 0.5 },
    innerRadius: 0,
    outerCenter: { x: 0.5, y: 0.5 },
    outerRadius: 0.5,
    colorStops: [
      { offset: 0, color: gray(1 - DUNGEON_AMBIENT_LEVEL) },
      { offset: 0.18, color: gray(1 - DUNGEON_AMBIENT_LEVEL) },
      { offset: 0.62, color: gray((1 - DUNGEON_AMBIENT_LEVEL) * 0.48) },
      { offset: 1, color: 0x000000 },
    ],
    textureSpace: 'local',
  });
  private readonly sightGradient = new FillGradient({
    type: 'radial',
    center: { x: 0.5, y: 0.5 },
    innerRadius: 0,
    outerCenter: { x: 0.5, y: 0.5 },
    outerRadius: 0.5,
    colorStops: [
      { offset: 0, color: 0xffffff },
      { offset: 0.72, color: 0xffffff },
      { offset: 1, color: 0x000000 },
    ],
    textureSpace: 'local',
  });
  private amount: number;
  private sightRun?: DungeonSightRun;
  private readonly filterArea = new Rectangle();
  private width = 0;
  private height = 0;
  private attached = false;

  constructor(
    private readonly app: Application,
    private readonly world: Container,
    private readonly lightTextures: LightTextures,
    initialAmount = 0,
  ) {
    this.amount = Math.min(1, Math.max(0, initialAmount));
    this.playerLight.rect(-1, -1, 2, 2).fill(this.playerGradient);
    this.playerLight.blendMode = 'add';
    this.lightLayer.addChild(this.ambient, this.staticLights, this.playerLight);
    this.sightStamp.circle(0, 0, 1).fill(this.sightGradient);
    this.sightStamp.blendMode = DUNGEON_SIGHT_BLEND_MODE;
    this.sightLayer.addChild(this.sightStamp);
  }

  /**
   * Ease toward the requested state and refresh the player light.
   * Returns true while the filter is actively drawing.
   */
  update(
    enabled: boolean,
    dtMs: number,
    playerScreen: { x: number; y: number },
    zoom: number,
    sight?: DungeonSightTarget,
    lights: readonly DungeonStaticLight[] = [],
  ): boolean {
    this.amount = advanceDungeonLight(this.amount, enabled, dtMs);
    this.updateSight(sight);
    if (this.amount === 0) {
      this.detach();
      return false;
    }

    this.resize();
    this.syncFilterArea();
    this.playerLight.position.set(
      playerScreen.x,
      playerScreen.y + DUNGEON_PLAYER_LIGHT_Y_OFFSET * zoom,
    );
    this.playerLight.scale.set(DUNGEON_PLAYER_LIGHT_RADIUS * zoom);
    this.drawStaticLights(lights, zoom);
    this.app.renderer.render({
      container: this.lightLayer,
      target: this.lightmap,
      clear: true,
      clearColor: 0x000000,
    });
    this.filter.resources.dungeonLightUniforms.uniforms.uStrength = this.amount;
    this.attach();
    return true;
  }

  private drawStaticLights(lights: readonly DungeonStaticLight[], zoom: number): void {
    while (this.staticLightSprites.length < lights.length) {
      const sprite = new Sprite();
      sprite.anchor.set(0.5);
      sprite.blendMode = 'add';
      this.staticLights.addChild(sprite);
      this.staticLightSprites.push(sprite);
    }
    for (let i = 0; i < this.staticLightSprites.length; i++) {
      const sprite = this.staticLightSprites[i]!;
      const light = lights[i];
      const asset = light
        ? resolveLightTexture(this.lightTextures, light.profile, light.shader)
        : undefined;
      sprite.visible = asset !== undefined;
      if (!light) continue;
      if (!asset) continue;
      sprite.texture = asset.texture;
      sprite.tint = asset.colored ? 0xffffff : light.color & 0xffffff;
      sprite.position.set(light.x, light.y);
      sprite.scale.set(zoom);
    }
  }

  destroy(): void {
    this.detach();
    this.filter.destroy();
    this.lightmap.destroy(true);
    this.sightMask.destroy(true);
    this.lightLayer.destroy({ children: true });
    this.sightLayer.destroy({ children: true });
    this.playerGradient.destroy();
    this.sightGradient.destroy();
  }

  private resize(): void {
    const width = Math.max(1, Math.round(this.app.screen.width));
    const height = Math.max(1, Math.round(this.app.screen.height));
    if (width === this.width && height === this.height) return;
    this.width = width;
    this.height = height;
    this.lightmap.resize(width, height, 1);
    this.ambient.clear().rect(0, 0, width, height).fill(gray(DUNGEON_AMBIENT_LEVEL));
  }

  private syncFilterArea(): void {
    const area = dungeonFilterArea(this.world.position, this.world.scale.x, this.app.screen);
    this.filterArea.x = area.x;
    this.filterArea.y = area.y;
    this.filterArea.width = area.width;
    this.filterArea.height = area.height;
    this.sightFrame[0] = area.x;
    this.sightFrame[1] = area.y;
    this.sightFrame[2] = area.width;
    this.sightFrame[3] = area.height;
  }

  private updateSight(target: DungeonSightTarget | undefined): void {
    if (!target) {
      this.sightRun = undefined;
      this.filter.resources.dungeonLightUniforms.uniforms.uSightEnabled = 0;
      return;
    }

    const plan = planDungeonSightStamp(this.sightRun, target);
    this.sightRun = plan.state;
    if (plan.clear) {
      this.sightMask.resize(target.mapWidth, target.mapHeight, 1);
      this.sightSize[0] = target.mapWidth;
      this.sightSize[1] = target.mapHeight;
    }
    this.sightStamp.position.set(target.playerTile.x + 0.5, target.playerTile.y + 0.5);
    this.sightStamp.scale.set(DUNGEON_SIGHT_RADIUS_TILES);
    this.app.renderer.render({
      container: this.sightLayer,
      target: this.sightMask,
      clear: plan.clear,
      clearColor: 0x000000,
    });
    this.filter.resources.dungeonLightUniforms.uniforms.uSightEnabled = 1;
  }

  private attach(): void {
    if (this.attached) return;
    const filters = this.world.filters ?? [];
    this.world.filters = [...filters, this.filter];
    this.world.filterArea = this.filterArea;
    this.attached = true;
  }

  private detach(): void {
    if (!this.attached) return;
    const filters = (this.world.filters ?? []).filter((filter) => filter !== this.filter);
    this.world.filters = filters.length > 0 ? filters : null;
    if (filters.length === 0) this.world.filterArea = undefined;
    this.attached = false;
  }
}
